import Images from "assets/images"
import { ASC } from "const"
import { CcRequirement, MtProject } from "server/models"
import {
    ModuleList,
    UserList,
    UserSettings,
    UserData,
    LangType,
    RequirementParentChildPdfExport,
    RequirementRevisionActiveList,
    RequirementStateList,
    RequirementHistoryList,
    RequirementHistoryNestedList,

    LoadCcRequirementTypes,
    LoadCcRequirementPriorities,
    LoadCcRequirementStatuses,
    LoadCcRequirementReviewMethods,
    LoadCcRequirementSmartCompliances,

    LoadCcApprovalLevels,
    LoadCcResponses,
    LoadAllCcRequirements,
    LoadAllCcRequirementParentChild,
    LoadCcRequirementRevisions,
    LoadCcRequirementState,
    LoadCcRequirementHistory,
    LoadCcRequirementParents,
    LoadCcRequirementChildren,
    PrepareRequirementsNestedIds,
    SortCcRequirement,
} from "client/store"
import { PrepareNestedHistory } from "client/components"

import pdfmake from "client/components/pdfmake/pdfmake"
import vfs from "client/components/pdfmake/vfs_fonts"
import fontList from "client/components/pdfmake/fontList"

import {
    ReportAccountLogo,
    ReportProjectLogo,
} from "client/components/pdfmake/template/functions"
import * as Const from "client/components/pdfmake/template/const"
import templateStyles from "client/components/pdfmake/template/styles"

import { Header, Footer } from "./headerFooter"
import TitlePage from "./titlePage"
import CopyrightPage from "./copyrightPage"
import OverviewPage from "./overviewPage"
import RequirementsPage from "./requirementsPage"


pdfmake.addVirtualFileSystem(vfs)
pdfmake.addFonts(fontList)

const RequirementExportToPDF = (
    requirementRevisionActiveList: RequirementRevisionActiveList,
    project: MtProject,
    moduleList: ModuleList,
    userList: UserList,
    userSettings: UserSettings,
    userData: UserData,
    lang: LangType,
    callback: (isLoading: boolean) => void,
) => {
    callback(true)

    Promise.all([
        // requirement dictionaries
        LoadCcRequirementTypes(userData.accountId, project.id)
        .then((res) => res),
        LoadCcRequirementPriorities(userData.accountId, project.id)
        .then((res) => res.priorityList),
        LoadCcRequirementStatuses(userData.accountId, project.id)
        .then((res) => res.statusList),
        LoadCcRequirementReviewMethods(userData.accountId, project.id)
        .then((res) => res),
        LoadCcRequirementSmartCompliances(userData.accountId, project.id)
        .then((res) => res),

        // requirement approval levels
        LoadCcApprovalLevels(userData.accountId, project.id, 3)
        .then((res) => res),

        // responsible responses
        LoadCcResponses(userData.accountId, project.id, 0)
        .then((res) => res.responseList),
        // requirement responses
        LoadCcResponses(userData.accountId, project.id, 4)
        .then((res) => res.responseList),

        LoadAllCcRequirements(project.id)
        .then((requirementList) => (
            LoadAllCcRequirementParentChild(project.id)
            .then((requirementParentChild) => {
                const requirementIds: number[] = []
                const requirementsSorted = SortCcRequirement(Object.values(requirementList), "uniqueId", ASC, {})
                requirementsSorted.forEach((requirement) => requirementIds.push(requirement.id))

                const nested = PrepareRequirementsNestedIds(requirementsSorted, requirementParentChild)

                const parentChildList: RequirementParentChildPdfExport = {}
                const requirementRevisionList: RequirementRevisionActiveList = {}
                const requirementStateList: RequirementStateList = {}
                const requirementHistoryList: RequirementHistoryList = {}
                const requirementHistoryNestedList: RequirementHistoryNestedList = {}
                const promiseArray: Promise<any>[] = []

                Object.values(requirementList).forEach((requirement: CcRequirement) => {
                    promiseArray.push(
                        // load parents/children
                        Promise.all([
                            LoadCcRequirementParents(requirement.id).then((res) => res),
                            LoadCcRequirementChildren(requirement.id).then((res) => res),
                        ]).then(([parents, children]) => {
                            parentChildList[requirement.id] = { parents, children }
                        }),

                        // load revision, state, history for requirement
                        LoadCcRequirementRevisions(requirement.id).then((revisions) => {
                            let revision = revisions[revisions.length - 1] // by default use the last revision
                            if (requirementRevisionActiveList && requirementRevisionActiveList[requirement.id]) {
                                revision = requirementRevisionActiveList[requirement.id]
                            }

                            requirementRevisionList[requirement.id] = revision

                            return Promise.all([
                                LoadCcRequirementState(requirement.id, revision ? revision.id : null).then((res) => {
                                    requirementStateList[requirement.id] = res
                                }),
                                LoadCcRequirementHistory(requirement.id, revision ? revision.id : null).then((res) => {
                                    requirementHistoryList[requirement.id] = res
                                    requirementHistoryNestedList[requirement.id] = PrepareNestedHistory(res)
                                }),
                            ])
                        }),
                    )
                })

                return Promise.all(promiseArray).then(() => ({
                    requirementList,
                    requirementIds,
                    nested,
                    parentChildList,

                    requirementRevisionList,
                    requirementStateList,
                    requirementHistoryList,
                    requirementHistoryNestedList,
                }))
            })
        )),
    ]).then(([
        typeList,
        priorityList,
        statusList,
        reviewMethodList,
        smartComplianceList,

        requirementLevelList,

        responsibleResponses,
        requirementResponses,

        requirementData,
    ]) => {
        Promise.all([
            // load all used images as base64 before creating pdf
            ReportAccountLogo(userData).then((res) => res),
            ReportProjectLogo(project, userSettings).then((res) => res),
        ]).then(([accountLogo, projectLogo]) => {
            const responseList = {
                ...responsibleResponses,
                ...requirementResponses,
            }

            const docDefinition = {
                info: {
                    title: lang.ccRequirementsReviewReport,
                    author: lang.risknowlogy,
                    subject: lang.result,
                    keywords: "CC, PDF, Requirements Review, Risknowlogy",
                },
                pageMargins: Const.PAGE_MARGINS,
                pageSize: "A4",
                images: {
                    accountLogo,
                    projectLogo,
                    downwardArrow: `${window.location.origin}${Images.downwardArrow}`,
                },
                defaultStyle: {
                    fontSize: 11,
                    font: "Arial",
                },
                styles: templateStyles,
                header: (currentPage: number) => Header(currentPage, project),
                footer: (currentPage: number, pageCount: number) => (
                    Footer(currentPage, pageCount, project, userData, lang)
                ),
                content: TitlePage(project, lang).concat(
                    CopyrightPage(moduleList, lang),
                    [{
                        pageBreak: "before",
                        toc: {
                            title: {
                                text: lang.tableOfContents,
                                style: Const.HEADER,
                                tocItem: true,
                                tocStyle: Const.TOC_HEADER,
                                tocMargin: [0, 5, 0, 0],
                            },
                            numberStyle: {
                                bold: true,
                                fontSize: 11,
                            },
                        },
                    }],
                    OverviewPage(
                        requirementData.requirementList,
                        requirementData.nested,
                        project,
                        lang,
                    ),
                    RequirementsPage(
                        requirementData.requirementList,
                        requirementData.requirementIds,
                        requirementData.parentChildList,

                        requirementData.requirementRevisionList,
                        requirementData.requirementStateList,
                        requirementData.requirementHistoryList,
                        requirementData.requirementHistoryNestedList,

                        typeList,
                        priorityList,
                        statusList,
                        reviewMethodList,
                        smartComplianceList,

                        requirementLevelList.levelList,
                        requirementLevelList.levelIds,
                        responseList,
                        userList,
                        lang,
                    ),
                ),
            }

            callback(false)
            pdfmake.createPdf(docDefinition).open()
        })
    })
}
export default RequirementExportToPDF
